import cls from 'classnames';
import React, {
    useEffect, useRef, useState
} from "react";
import {
    agentService, FileItem, UploadFileResponse
} from "../../api/agentService";
import NewChatIcon from '../../assets/icons/new_chat.svg';
import FileItemComp from '../FileItem';
import Icon from "../Icon";
import Tooltip from '../Tooltip';
import styles from './styles.module.scss';

// 新增类型
export type UploadType = 'image' | 'file';

// 支持的文件格式
const SUPPORTED_FILE_TYPES = '.png,.jpg,.jpeg,.pdf,.bmp,.tiff,.webp,.doc,.docx,.html,.mhtml,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.ofd,.rtf';

// 上传文件状态类型
interface UploadFileState {
    file: File;
    file_url?: string;
    file_type?: string;
    isUploading: boolean;
    error?: string;
}

export interface ContextItem {
    payload: any;
    canDelete?: boolean;
    title: string;
    type: 'file' | 'dashboard'
}

export type handleSearch = (inputValue: string, uploadedFiles: FileItem[], context?: ContextItem[]) => void;

// 提取 props 类型定义
export interface AgentInputProps {
    style?: React.CSSProperties;
    className?: string;
    handleSearch: handleSearch;
    onNewChat?: () => void;
    placeholder?: string;
    clearAfterSearch?: boolean;
    rows?: number;
    showUpload?: boolean;
    uploadType?: UploadType;
    isSearching?: boolean;
    onStopSearch?: () => void;
    context?: ContextItem[];
    // 新增
    inputValue?: string;
    setInputValue?: (v: string) => void;
    uploadFiles?: FileItem[];
    setUploadFiles?: React.Dispatch<React.SetStateAction<FileItem[]>>;
    onContextRemove?: (index: number) => void;
}

export const AgentInput = ({
    style,
    className,
    handleSearch,
    onNewChat,
    placeholder,
    clearAfterSearch = true,
    rows = 1,
    showUpload = true,
    uploadType = 'image',
    isSearching = false,
    onStopSearch,
    inputValue: inputValueProp,
    setInputValue: setInputValueProp,
    context = [],

    uploadFiles,
    setUploadFiles,
    onContextRemove,
    textareaRef: externalTextareaRef,
}: AgentInputProps & { textareaRef?: React.RefObject<HTMLTextAreaElement> }) => {
    // 兼容外部和本地 inputValue
    const [localInputValue, localSetInputValue] = useState('');
    const inputValue = inputValueProp !== undefined ? inputValueProp : localInputValue;
    const setInputValue = setInputValueProp !== undefined ? setInputValueProp : localSetInputValue;

    const fileInputRef = useRef<HTMLInputElement>(null);
    const internalTextareaRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = (externalTextareaRef || internalTextareaRef) as React.RefObject<HTMLTextAreaElement>;
    const [_uploadedFiles, _setUploadedFiles] = useState<FileItem[]>([]);
    const [uploadStates, setUploadStates] = useState<UploadFileState[]>([]);
    const uploadedFiles = uploadFiles || _uploadedFiles;
    const setUploadedFiles = setUploadFiles || _setUploadedFiles;
    
    useEffect(() => {
        textareaRef.current?.focus();
    }, [])

    // 上传文件函数
    const uploadFile = async (file: File): Promise<FileItem> => {
        try {
            const response: UploadFileResponse = await agentService.uploadFile(file);
            if (response.success) {
                return {url: response.file_url, file_type: response.file_type};
            } else {
                throw new Error(response.message || '上传失败');
            }
        } catch (error) {
            console.error('文件上传失败:', error);
            throw error;
        }
    };

    // 处理文件上传
    const handleFileUpload = async (files: FileList) => {
        const newFiles = Array.from(files);
        const filteredFiles = newFiles; // 允许上传任何类型的文件
        
        // 创建上传状态
        const newUploadStates: UploadFileState[] = filteredFiles.map(file => ({
            file,
            isUploading: true,
        }));
            
        setUploadStates(prev => [...prev, ...newUploadStates]);
        setUploadedFiles((prev: any) => [...prev, ...filteredFiles]);

        // 逐个上传文件
        for (let i = 0; i < newUploadStates.length; i++) {
            const file = newUploadStates[i].file;
            try {
                const uploadedFile = await uploadFile(file);
                    
                // 更新上传状态 - 通过文件对象查找，同时保存上传结果
                setUploadStates(prev => prev.map(item => {
                    if (item.file === file) {
                        return {
                            ...item, 
                            isUploading: false,
                            file_url: uploadedFile.url,
                            file_type: uploadedFile.file_type || file.type
                        };
                    }
                    return item;
                }));
                    
                // 更新 uploadedFiles，将文件替换为 FileItem - 通过文件对象查找
                setUploadedFiles((prev: any) => prev.map((item: any) => {
                    if (item === file) {
                        return {
                            url: uploadedFile.url,
                            file_type: uploadedFile.file_type || file.type
                        };
                    }
                    return item;
                }));
                    
            } catch (error) {
                // 更新错误状态 - 通过文件对象查找
                setUploadStates(prev => prev.map(item => {
                    if (item.file === file) {
                        return { 
                            ...item, 
                            isUploading: false, 
                            error: error instanceof Error ? error.message : '' 
                        };
                    }
                    return item;
                }));
                    
            }
        }

    };

    const searchConfirm = () => {
        console.log('uploadedFiles', uploadedFiles)
        handleSearch(inputValue, uploadedFiles, context);
        
        if (clearAfterSearch) {
            setInputValue('');
            setUploadedFiles([]);
            setUploadStates([]);
        }
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    }

    const handleStopSearch = () => {
        onStopSearch?.();
    }

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            handleFileUpload(e.target.files);
            e.target.value = '';
            textareaRef.current?.focus();
        }
    };

    const removeFile = (idx: number) => {
        const fileToRemove = uploadedFiles[idx];
        setUploadedFiles((prev: any) => prev.filter((_: any, i: any) => i !== idx));
        
        // 通过文件对象或 FileItem 查找对应的状态
        setUploadStates(prev => prev.filter(state => {
            if (typeof fileToRemove === 'object' && 'url' in fileToRemove) {
                // 如果是 FileItem，检查是否匹配
                return state.file_url !== fileToRemove.url;
            } else {
                // 如果是文件对象，检查是否匹配
                return state.file !== fileToRemove;
            }
        }));
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        const items = e.clipboardData?.items;
        if (!items) return;
        for (const item of items) {
            if (item.type.indexOf('image') !== -1) {
                e.preventDefault();
                const file = item.getAsFile();
                if (file) {
                    // 手动创建 FileList 对象
                    const dt = new DataTransfer();
                    dt.items.add(file);
                    handleFileUpload(dt.files);
                }
            }
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setInputValue(value);
        const textarea = textareaRef.current;
        if (textarea) {
            // 重置高度以获取正确的 scrollHeight
            textarea.style.height = 'auto';
            // 计算新高度，保留换行符
            const newHeight = Math.min(textarea.scrollHeight, 120);
            textarea.style.height = newHeight + 'px';
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        const isComposing = e.nativeEvent.isComposing;
        // 如果按下 Enter 键且没有按住 Shift 键，且不在输入法组合状态，则提交
        if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
            e.preventDefault();
            searchConfirm();
        }
        // 如果按下 Shift+Enter，允许插入换行符（默认行为）
    };

    // 获取文件显示信息
    const getFileDisplayInfo = (fileOrItem: File | FileItem, index: number) => {
        if (typeof fileOrItem === 'object' && 'url' in fileOrItem) {
            // FileItem，需要从 uploadStates 中找到对应的文件信息
            const uploadState = uploadStates.find((state) => {
                // 优先通过 file_url 匹配
                return state.file_url === fileOrItem.url;
            });
            
            if (uploadState) {
                return {
                    file: uploadState.file,
                    isUploading: uploadState.isUploading,
                    error: uploadState.error,
                    isFileItem: true,
                    fileItem: fileOrItem
                };
            } else {
                // 如果没有找到对应的状态，说明可能是非上传模式的文件
                return {
                    file: new File([""], fileOrItem.url, { type: fileOrItem.file_type }),
                    isUploading: false,
                    error: undefined,
                    fileItem: fileOrItem,
                    isFileItem: true
                };
            }
        } else {
            // 文件对象，需要从 uploadStates 中找到对应的状态
            const uploadState = uploadStates.find((state) => {
                // 通过文件对象匹配
                return state.file === fileOrItem;
            });
            
            if (uploadState) {
                return {
                    file: fileOrItem,
                    isUploading: uploadState.isUploading,
                    error: uploadState.error,
                    isFileItem: false
                };
            }
        }
    };

    // 检查是否有正在上传的文件
    const hasUploadingFiles = uploadStates.some(state => state.isUploading);

    const canSend = (inputValue.trim() || uploadedFiles.length > 0) && !isSearching && !hasUploadingFiles;

    return (
        <div
            style={style}
            className={
                `${styles.inputContainer} ${className}`
            }
        >
            <div className={styles.searchBox} style={uploadedFiles.length ? {paddingTop: '16px'} : {}}>
                {context?.map((item, idx) => (
                    <div key={idx} className={styles.contextItem}>
                        <Icon name={item.type === 'file' ? "artifact" : "dashboard"}></Icon>
                        <span>{item.title}</span>
                        {onContextRemove && item.canDelete ? (
                            <button className={styles.removeContext} onClick={() => onContextRemove(idx)}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        ) : null}
                    </div>
                ))}
                {(uploadedFiles.length) ? (
                    <div className={styles.filePreview}>
                        {uploadedFiles.map((fileOrItem, idx) => {
                            const fileInfo = getFileDisplayInfo(fileOrItem, idx);
                            if (!fileInfo) return null;

                            // 判断文件类型：优先使用上传后的file_type，其次使用原始文件type
                            const fileType = (
                                fileInfo.isFileItem && fileInfo.fileItem?.file_type)
                                || fileInfo.file.type;
                            const isImage = fileType.startsWith('image/');

                            return (
                                <div key={idx} className={styles.fileItem}>
                                    {isImage ? (
                                        <div className={styles.imageContainer}>
                                            <img
                                                src={fileInfo.isFileItem && fileInfo.fileItem
                                                    ? fileInfo.fileItem.url : URL.createObjectURL(fileInfo.file)}
                                                alt={fileInfo.file.name}
                                            />
                                            {fileInfo.isUploading ? (
                                                <div className={styles.uploadingOverlay}>
                                                    <div className={styles.spinner}></div>
                                                </div>
                                            ) : null}
                                            {fileInfo.error ? (
                                                <div className={styles.errorOverlay}>
                                                    <span>Upload failed</span>
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : (
                                        <div className={styles.fileIcon}>
                                            <FileItemComp
                                                fileItem={{
                                                    file_type: fileInfo.file?.type,
                                                    url: fileInfo.fileItem?.url || '',
                                                    name: fileInfo.file.name,
                                                    size: fileInfo.file.size
                                                }}
                                            />
                                            {fileInfo.isUploading ? (
                                                <div className={styles.uploadingOverlay}>
                                                    <div className={styles.spinner}></div>
                                                </div>
                                            ) : null}
                                            {fileInfo.error ? (
                                                <div className={styles.errorOverlay}>
                                                    <span>Upload failed</span>
                                                </div>
                                            ) : null}
                                        </div>
                                    )}
                                    <button className={styles.removeFile} onClick={() => removeFile(idx)}>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                </div>
                            );
                        })}

                    </div>
                ) : null}
                <div className={styles.inputRow}>
                    {/* 上传按钮 */}
                    {showUpload ? (
                        <>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileInput}
                                multiple
                                accept={SUPPORTED_FILE_TYPES}
                                className={styles.fileInput}
                            />
                            <button
                                className={cls(styles.uploadBtn, styles.actionBtn)}
                                onClick={triggerFileInput}
                                title={uploadType === 'image' ? 'Upload image' : 'Upload file'}
                            >
                                <Icon name="plus_outline" />
                            </button>
                        </>
                    ) : null}
                    {onNewChat ? (
                        <button
                            className={styles.newChatBtn}
                            onClick={() => {
                                textareaRef.current?.focus();
                                onNewChat();
                            }}
                            title="New chat"
                        >
                            <img src={NewChatIcon} alt="New chat" />
                        </button>
                    ) : null}
                    <textarea
                        className={styles.inputWrapper}
                        ref={textareaRef}
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        placeholder={placeholder}
                        rows={rows}
                    />
                    {!isSearching && (
                        <button
                            className={cls(styles.searchBtn)}
                            onClick={searchConfirm}
                            disabled={!canSend}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M7.83191 12.833C7.83191 13.2932 7.45881 13.6663 6.99857 13.6663C6.53833 13.6663 6.16524 13.2932 6.16524 12.833L6.16524 1.16634C6.16524 0.706104 6.53834 0.333008 6.99857 0.333008C7.45881 0.333008 7.83191 0.706104 7.83191 1.16634L7.83191 12.833Z" fill="inherit" />
                                <path d="M13.4212 6.41052C13.7466 6.73596 13.7466 7.26347 13.4212 7.58891C13.0957 7.91434 12.5682 7.91434 12.2428 7.58891L6.99864 2.34476L1.75449 7.58891C1.42906 7.91434 0.901546 7.91434 0.576109 7.58891C0.250672 7.26347 0.250672 6.73596 0.576109 6.41052L6.40944 0.577187C6.73488 0.25175 7.26239 0.25175 7.58783 0.577187L13.4212 6.41052Z" fill="inherit" />
                            </svg>
                        </button>
                    )}
                    {isSearching && onStopSearch ? (
                        <Tooltip content="Stop">
                            <button
                                className={styles.fabStopBtn}
                                onClick={handleStopSearch}
                            >
                                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M0 14C0 6.26801 6.26801 0 14 0C21.732 0 28 6.26801 28 14C28 21.732 21.732 28 14 28C6.26801 28 0 21.732 0 14Z" fill="black" fillOpacity="0.16" />
                                    <rect x="9" y="9" width="10" height="10" rx="2" fill="#636E83" />
                                </svg>
                            </button>
                        </Tooltip>
                    ) : null}
                </div>
            </div>
        </div>
    )
};